import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  MessageCircle, Loader2, Send, Search, UserCheck, Clock, RotateCw,
  CheckCheck, Check, AlertTriangle, Users, X, NotebookPen,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtWait = (mins) => {
  if (!mins || mins <= 0) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

// Best CRM context for a conversation (for template sends).
const convContext = (c) => {
  if (!c) return null;
  if (c.tripId) return { contextType: 'trip', contextId: c.tripId };
  if (c.invoiceNumber) return { contextType: 'invoice', contextId: c.invoiceNumber };
  if (c.leadId) return { contextType: 'lead', contextId: c.leadId };
  if (c.travellerUcc) return { contextType: 'traveller', contextId: c.travellerUcc };
  return null;
};

const StatusTick = ({ status }) => {
  if (status === 'manually_logged') return <NotebookPen className="h-3.5 w-3.5 text-amber-500" />;
  if (status === 'read') return <CheckCheck className="h-3.5 w-3.5 text-sky-500" />;
  if (status === 'delivered') return <CheckCheck className="h-3.5 w-3.5 text-slate-400" />;
  if (status === 'accepted' || status === 'sent') return <Check className="h-3.5 w-3.5 text-slate-400" />;
  if (status === 'failed') return <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />;
  return <Clock className="h-3.5 w-3.5 text-slate-300" />;
};

const ConversationRow = ({ conv, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`wa-conv-row-${conv.id}`}
    className={`w-full text-left px-3 py-3 border-b border-slate-100 transition-colors ${active ? 'bg-[#F0FDF9]' : 'hover:bg-slate-50'}`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="font-semibold text-slate-800 text-sm truncate">{conv.contactName || conv.waId}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        {conv.needsResponse && conv.waitMinutes > 0 && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" data-testid={`wa-wait-${conv.id}`}>
            {fmtWait(conv.waitMinutes)}
          </span>
        )}
        {conv.unreadCount > 0 && (
          <span className="w-2 h-2 rounded-full bg-emerald-500" data-testid={`wa-unread-${conv.id}`} />
        )}
      </div>
    </div>
    <p className="text-xs text-slate-500 truncate mt-0.5">{conv.lastMessagePreview || '—'}</p>
    <div className="flex items-center gap-2 mt-1">
      {conv.needsResponse && <span className="text-[10px] text-amber-600 font-medium">Needs response</span>}
      {conv.assigned_to_name && <span className="text-[10px] text-slate-400">· {conv.assigned_to_name}</span>}
    </div>
  </button>
);

const MessageBubble = ({ msg, onRetry }) => {
  const out = msg.direction === 'outbound';
  const manual = msg.status === 'manually_logged';
  return (
    <div className={`flex ${out ? 'justify-end' : 'justify-start'}`} data-testid={`wa-msg-${msg.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
        manual ? 'bg-amber-50 border border-amber-200' : out ? 'bg-[#dcf8c6] text-slate-800' : 'bg-white border border-slate-200 text-slate-800'
      }`}>
        {msg.templateName && <p className="text-[10px] font-semibold text-emerald-700 mb-0.5">{msg.templateName}</p>}
        {manual && (
          <p className="text-[10px] font-semibold text-amber-700 mb-0.5 flex items-center gap-1" data-testid={`wa-msg-manual-tag-${msg.id}`}>
            <NotebookPen className="h-3 w-3" /> Manually logged — sent via WhatsApp app, not verified by Meta
          </p>
        )}
        <p className="whitespace-pre-line">{msg.body}</p>
        <div className="flex items-center justify-end gap-1 mt-1">
          {msg.status === 'failed' && (
            <button onClick={() => onRetry(msg)} className="text-[10px] text-rose-600 flex items-center gap-0.5 mr-1" data-testid={`wa-msg-retry-${msg.id}`}>
              <RotateCw className="h-3 w-3" /> Retry
            </button>
          )}
          {out && <StatusTick status={msg.status} />}
        </div>
        {msg.status === 'failed' && msg.failureReason && (
          <p className="text-[10px] text-rose-500 mt-0.5">{msg.failureReason}</p>
        )}
      </div>
    </div>
  );
};

// ---- Bulk send dialog ------------------------------------------------------
const BulkSendDialog = ({ open, onOpenChange }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTemplateId(''); setQuery(''); setResults([]); setSelected([]); setOutcome(null);
    axios.get(`${BACKEND_URL}/api/whatsapp/templates`, { params: { sendable: true } })
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/trips`, { params: { search: query.trim(), limit: 8 } });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const toggle = (t) => {
    const id = t.displayTripId || t.tripId;
    setSelected((prev) => prev.find((x) => x.id === id) ? prev.filter((x) => x.id !== id) : [...prev, { id, name: t.guestName }]);
  };

  const send = async () => {
    if (!templateId) { toast.error('Pick a template'); return; }
    if (selected.length === 0) { toast.error('Select at least one trip'); return; }
    setSending(true); setOutcome(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/bulk-send`, { templateId, tripIds: selected.map((s) => s.id) });
      setOutcome(r.data);
      toast.success(`Sent ${r.data.sent}/${r.data.total}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk send failed'); }
    finally { setSending(false); }
  };

  const outcomeRows = outcome ? (outcome.results || []) : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="wa-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk send to trips</DialogTitle>
          <DialogDescription>One logged message per recipient — not a broadcast. Rules apply per trip.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Approved template</label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-10" data-testid="wa-bulk-template"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.displayName} · {t.category}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Add trips</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, Trip ID or destination…" className="h-10 pl-9" data-testid="wa-bulk-search" />
            </div>
            {results.length > 0 && (
              <div className="mt-1 rounded-lg border border-slate-200 bg-white overflow-hidden">
                {results.map((t) => (
                  <button key={t.id || t.tripId} type="button" onClick={() => toggle(t)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 text-sm">
                    {t.guestName || 'Trip'} <span className="text-xs text-slate-400 font-mono ml-1">{t.displayTripId || ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="wa-bulk-selected">
              {selected.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                  {s.name || s.id} <button onClick={() => setSelected((p) => p.filter((x) => x.id !== s.id))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          {outcome && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="wa-bulk-outcome">
              <p className="font-semibold">Sent {outcome.sent} · Failed/Skipped {outcome.failed} of {outcome.total}</p>
              <div className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                {outcomeRows.map((r) => (
                  <p key={r.tripId} className={`text-xs ${r.ok ? 'text-emerald-700' : 'text-rose-600'}`}>
                    {r.tripId}: {r.ok ? 'sent' : (typeof r.reason === 'string' ? r.reason : r.status)}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-bulk-send-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send to {selected.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Inbox page ------------------------------------------------------------
const WhatsAppInbox = () => {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeId, setActiveId] = useState('');
  const [thread, setThread] = useState({ conversation: null, messages: [] });
  const [reply, setReply] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  const [sending, setSending] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadConvs = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/whatsapp/conversations`, { params: { filter } });
      setConvs(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Failed to load conversations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadConvs(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/whatsapp/quick-replies`).then((r) => setQuickReplies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const openConv = async (c) => {
    setActiveId(c.id);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/whatsapp/conversations/${c.id}/messages`);
      setThread(r.data || { conversation: null, messages: [] });
      if (c.unreadCount > 0 || c.needsResponse) {
        await axios.post(`${BACKEND_URL}/api/whatsapp/conversations/${c.id}/read`).catch(() => {});
        loadConvs();
      }
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to open conversation'); }
  };

  const claim = async () => {
    if (!activeId) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/conversations/${activeId}/assign`, {});
      toast.success(`Assigned to ${r.data.assigned_to_name}`);
      loadConvs(); openConv({ id: activeId });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/conversations/${activeId}/reply`, { text: reply.trim() });
      if (r.data?.ok) { toast.success('Reply sent'); setReply(''); }
      else { toast.error(r.data?.reason || 'Send failed'); }
      openConv({ id: activeId });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const retryMsg = async (msg) => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/messages/${msg.id}/retry`);
      if (r.data?.ok) toast.success('Retried'); else toast.error(r.data?.reason || 'Retry failed');
      openConv({ id: activeId });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
  };

  const conv = thread.conversation;
  const threadMessages = thread.messages || [];
  const ctx = useMemo(() => convContext(conv), [conv]);
  const sessionOpen = conv?.sessionOpen;

  return (
    <div className="p-4 sm:p-6" data-testid="whatsapp-inbox-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-emerald-600" /> WhatsApp
          </h1>
          <p className="text-sm text-slate-500">Transactional conversations tied to CRM records.</p>
        </div>
        <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="wa-bulk-open-btn">
          <Users className="h-4 w-4 mr-1.5" /> Bulk send
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[480px]">
        {/* List */}
        <div className="rounded-xl border border-slate-200 bg-white flex flex-col overflow-hidden">
          <div className="flex gap-1 p-2 border-b border-slate-100">
            <button onClick={() => setFilter('all')} data-testid="wa-filter-all" className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${filter === 'all' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>All</button>
            <button onClick={() => setFilter('unread')} data-testid="wa-filter-unread" className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${filter === 'unread' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Needs response</button>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="wa-conversation-list">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : convs.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No conversations yet.</div>
            ) : (
              convs.map((c) => <ConversationRow key={c.id} conv={c} active={c.id === activeId} onClick={() => openConv(c)} />)
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="rounded-xl border border-slate-200 bg-[#efeae2] flex flex-col overflow-hidden">
          {!conv ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
                <div>
                  <p className="font-semibold text-slate-800">{conv.contactName || conv.waId}</p>
                  <p className="text-xs text-slate-400">
                    {conv.waId}{conv.assigned_to_name ? ` · ${conv.assigned_to_name}` : ' · unassigned'}
                    {sessionOpen ? ' · session open' : ' · session closed'}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={claim} data-testid="wa-claim-btn"><UserCheck className="h-4 w-4 mr-1" /> Assign to me</Button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="wa-thread-messages">
                {threadMessages.map((m) => <MessageBubble key={m.id} msg={m} onRetry={retryMsg} />)}
                <div ref={bottomRef} />
              </div>

              <div className="bg-white border-t border-slate-200 p-3">
                {quickReplies.length > 0 && sessionOpen && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="wa-quick-replies">
                    {quickReplies.map((q) => (
                      <button key={q.id} onClick={() => setReply((r) => (r ? r + ' ' : '') + q.body)} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid={`wa-qr-${q.id}`}>
                        {q.title}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={1}
                    placeholder={sessionOpen ? 'Type a reply…' : 'Session closed — use an approved template →'}
                    disabled={!sessionOpen}
                    className="text-sm resize-none min-h-[40px]"
                    data-testid="wa-reply-input"
                  />
                  <Button onClick={sendReply} disabled={sending || !sessionOpen || !reply.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="wa-reply-send">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" onClick={() => ctx ? setTplOpen(true) : toast.error('Link this contact to a trip/traveller to send a template')} className="shrink-0" data-testid="wa-thread-template-btn">
                    Template
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {tplOpen && ctx && (
        <WhatsAppSendDialog open={tplOpen} onOpenChange={(o) => { setTplOpen(o); if (!o) openConv({ id: activeId }); }} contextType={ctx.contextType} contextId={ctx.contextId} title={conv?.contactName} />
      )}
      <BulkSendDialog open={bulkOpen} onOpenChange={setBulkOpen} />
    </div>
  );
};

export default WhatsAppInbox;
